import { isoDateTime, z } from "../zod.js";
import { Problem } from "../common/problem.js";
import { PlanningDate, PlanningRowStatus } from "./schemas.js";

// Module 03 planning, P3-H-1: the calendars contract (spec 03 §2.12 to §2.14,
// §2.22, §3.3a and §5 rows 26 to 37; D-386, D-390). A segment's content rules
// (weekday, wall time, efficiency, overlap) are the calendar kernel's and
// answer 422 `SHIFT_PATTERN_INVALID` naming each failing segment, so the
// schema here checks only the shape and leaves those values to the service.
// Lists answer `{items, next_cursor}`.

const Uuid = z.guid();

/** The first and last business dates the calendar kernel reads (spec 03 §3.3a). */
export const CALENDAR_MIN_DATE = "1900-01-01";
export const CALENDAR_MAX_DATE = "9999-12-31";

/**
 * A business date the calendar kernel can read: `PlanningDate` from
 * 1900-01-01. An assignment dated earlier would be stored and then refused by
 * every preview and MRP run that reads it (D-393 ruling 4).
 */
export const CalendarDate = PlanningDate.refine((d) => d >= CALENDAR_MIN_DATE, {
  message: `a date from ${CALENDAR_MIN_DATE}`,
  params: { reason: "date_out_of_range" },
});
const Timestamp = isoDateTime();
const PageCursor = z.string().optional();
const PageLimit = z.coerce.number().int().min(1).max(200).optional();

/** A pattern code: `2SHIFT_12H`, `DAY_ONLY`. Upper case, digits, `_` and `-`, at most 64. */
export const ShiftPatternCode = z
  .string()
  .regex(/^[A-Z0-9][A-Z0-9_-]{0,63}$/)
  .openapi({ example: "2SHIFT_12H" });

/** A trimmed name of 1 to 120 characters. */
const ShiftPatternName = z
  .string()
  .trim()
  .min(1)
  .max(120)
  .openapi({ example: "Two 12-hour shifts" });

/** The most segments one pattern carries: six a day for a week. */
export const SHIFT_PATTERN_MAX_SEGMENTS = 42;

/**
 * One segment as `shift_patterns.segments` stores it (spec 03 §2.12). The
 * values are checked by the calendar kernel, so a weekday of 8 or a start of
 * `25:00` is a 422 naming the segment, not a 400.
 */
export const ShiftSegment = z
  .object({
    /** ISO weekday the segment starts on, Monday 1 to Sunday 7. */
    weekday: z.number().int(),
    /** Local wall-clock start, `HH:MM`. */
    start: z.string().max(16),
    /** Local wall-clock end, `HH:MM`; before `start` runs into the next day. */
    end: z.string().max(16),
    /**
     * The shift this segment is, such as `DAY`, matched against module 04's
     * definitions. Trimmed, so `"DAY "` is stored and matched as `DAY`.
     */
    shift_code: z.string().trim().max(32),
    /** Whole per cent of standard output, 10 to 200. */
    efficiency_pct: z.number(),
  })
  .strict()
  .openapi("ShiftSegment");

const ShiftSegments = z.array(ShiftSegment).max(SHIFT_PATTERN_MAX_SEGMENTS);

export const ShiftPattern = z
  .object({
    id: Uuid,
    code: ShiftPatternCode,
    name: z.string(),
    segments: z.array(ShiftSegment),
    status: PlanningRowStatus,
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("ShiftPattern");

export const ShiftPatternListQuery = z.object({
  status: PlanningRowStatus.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const ShiftPatternListResponse = z
  .object({ items: z.array(ShiftPattern), next_cursor: z.string().nullable() })
  .openapi("ShiftPatternListResponse");

export const ShiftPatternCreateRequest = z
  .object({ code: ShiftPatternCode, name: ShiftPatternName, segments: ShiftSegments })
  .strict()
  .openapi("ShiftPatternCreateRequest");

/** `PATCH /v1/shift-patterns/{id}`: any of name, segments and status, with the row's `updated_at`. */
export const ShiftPatternPatchRequest = z
  .object({
    name: ShiftPatternName.optional(),
    segments: ShiftSegments.optional(),
    status: PlanningRowStatus.optional(),
    expected_updated_at: Timestamp,
  })
  .strict()
  .openapi("ShiftPatternPatchRequest");

export const ShiftPatternIdParam = z.object({ id: Uuid });

/** The reasons the calendar kernel refuses a segment (spec 03 §3.3a). */
export const ShiftSegmentIssueReason = z.enum([
  "no_segments",
  "weekday_out_of_range",
  "start_malformed",
  "end_malformed",
  "shift_code_missing",
  "efficiency_out_of_range",
  "zero_length",
  "overlaps",
]);

export const ShiftSegmentIssue = z
  .object({
    /** The failing segment; null for a pattern with no segments. */
    index: z.number().int().nullable(),
    reason: ShiftSegmentIssueReason,
    /** For `overlaps`, the segment it overlaps. */
    other_index: z.number().int().optional(),
  })
  .openapi("ShiftSegmentIssue");

/** 422 `SHIFT_PATTERN_INVALID` lists every failing segment, ordered by index. */
export const ShiftPatternInvalidProblem = Problem.extend({
  issues: z.array(ShiftSegmentIssue).optional(),
}).openapi("ShiftPatternInvalidProblem");

/**
 * 409 on a pattern: `SHIFT_PATTERN_CHANGED` carries the stored row as
 * `current`; `SHIFT_PATTERN_IN_USE` names the open assignments and the
 * facilities whose calendar is the pattern; `SHIFT_PATTERN_CODE_TAKEN` carries
 * neither.
 */
export const ShiftPatternConflictProblem = Problem.extend({
  current: ShiftPattern.optional(),
  assignment_ids: z.array(Uuid).optional(),
  facility_ids: z.array(Uuid).optional(),
}).openapi("ShiftPatternConflictProblem");

/** A facility outside the caller's scope that uses a pattern (spec 03 §4.8). */
export const ShiftPatternUseOutsideScope = z
  .object({ facility_id: Uuid, facility_name: z.string() })
  .openapi("ShiftPatternUseOutsideScope");

/** 403 on a pattern PATCH: the code itself, or `SHIFT_PATTERN_OUT_OF_SCOPE` naming the facilities. */
export const ShiftPatternForbiddenProblem = Problem.extend({
  missing_permission: z.string().optional(),
  facilities: z.array(ShiftPatternUseOutsideScope).optional(),
}).openapi("ShiftPatternForbiddenProblem");

// --- §2.13 work-centre calendars ---------------------------------------------

export const WorkCenterCalendar = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    work_center_id: Uuid,
    shift_pattern_id: Uuid,
    shift_pattern_code: ShiftPatternCode,
    shift_pattern_name: z.string(),
    valid_from: PlanningDate,
    /** The last date covered; null for an open-ended assignment. */
    valid_to: PlanningDate.nullable(),
    /**
     * The pattern's shift codes that have no active shift definition in module
     * 04 at the facility, sorted; empty when every code has one. A warning
     * only: a save is never refused for it (spec 03 §2.12, D-386 ruling 8).
     */
    missing_shift_codes: z.array(z.string()),
    created_at: Timestamp,
    updated_at: Timestamp,
  })
  .openapi("WorkCenterCalendar");

export const WorkCenterCalendarListQuery = z.object({
  facility_id: Uuid,
  work_center_id: Uuid.optional(),
  cursor: PageCursor,
  limit: PageLimit,
});

export const WorkCenterCalendarListResponse = z
  .object({ items: z.array(WorkCenterCalendar), next_cursor: z.string().nullable() })
  .openapi("WorkCenterCalendarListResponse");

const rangeOrdered = (
  body: { valid_from?: string | undefined; valid_to?: string | null | undefined },
  ctx: z.RefinementCtx,
): void => {
  if (
    body.valid_from !== undefined &&
    body.valid_to !== undefined &&
    body.valid_to !== null &&
    body.valid_to < body.valid_from
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["valid_to"],
      message: "valid_to may not be before valid_from",
      params: { reason: "valid_to_before_valid_from" },
    });
  }
};

export const WorkCenterCalendarCreateRequest = z
  .object({
    facility_id: Uuid,
    work_center_id: Uuid,
    shift_pattern_id: Uuid,
    valid_from: CalendarDate,
    valid_to: CalendarDate.nullable().optional(),
  })
  .strict()
  .superRefine(rangeOrdered)
  .openapi("WorkCenterCalendarCreateRequest");

/** `PATCH /v1/work-center-calendars/{id}`: the last covered date; null reopens the assignment. */
export const WorkCenterCalendarPatchRequest = z
  .object({ valid_to: CalendarDate.nullable() })
  .strict()
  .openapi("WorkCenterCalendarPatchRequest");

export const WorkCenterCalendarIdParam = z.object({ id: Uuid });

/** 409 `OVERLAPPING_ASSIGNMENT` names the assignment whose range the new one meets. */
export const OverlappingAssignment = z
  .object({ id: Uuid, valid_from: PlanningDate, valid_to: PlanningDate.nullable() })
  .openapi("OverlappingAssignment");

export const WorkCenterCalendarConflictProblem = Problem.extend({
  overlapping: OverlappingAssignment.optional(),
}).openapi("WorkCenterCalendarConflictProblem");

// --- §2.14 calendar exceptions -----------------------------------------------

export const CalendarExceptionKind = z.enum([
  "holiday",
  "planned_maintenance",
  "breakdown",
  "extra_shift",
  "overtime",
]);

/** A `numeric(5,2)` factor as a decimal string, such as `"0.00"` or `"1.50"`. */
export const CapacityFactor = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,2})?$/)
  .openapi({ example: "0.00", description: "0 to 3 with at most two decimals; 0 closes" });

/**
 * An instant with an explicit zone, exactly as the calendar kernel reads one
 * (`packages/scheduler/src/calendar/exceptions.ts`): `YYYY-MM-DDTHH:MM`, then
 * optional seconds with at most three decimals, then `Z` or a `±HH:MM`
 * offset. A form the kernel refuses (`+0500`, microseconds) is a 400 here,
 * never the 422 of spec 03 §3.3b; `planning-calendar-contract.test.ts` holds
 * the two to one grammar. The year starts at 1900, as `CalendarDate` does.
 */
export const CALENDAR_INSTANT_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d)(?:\.\d{1,3})?)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/;
const ExceptionInstant = z
  .string()
  .regex(CALENDAR_INSTANT_PATTERN)
  .check(isoDateTime({ offset: true }))
  .refine((v) => v.slice(0, 10) >= CALENDAR_MIN_DATE, {
    message: `an instant from ${CALENDAR_MIN_DATE}`,
    params: { reason: "date_out_of_range" },
  });

export const CalendarException = z
  .object({
    id: Uuid,
    facility_id: Uuid,
    /** null for a facility-wide row, such as a public holiday. */
    work_center_id: Uuid.nullable(),
    starts_at: Timestamp,
    ends_at: Timestamp,
    kind: CalendarExceptionKind,
    capacity_factor: CapacityFactor,
    note: z.string().nullable(),
    /** `manual` alone until module 08's writer (spec 03 §2.14). */
    source: z.literal("manual"),
    created_at: Timestamp,
  })
  .openapi("CalendarException");

/** The widest date window one exceptions list reads. */
export const CALENDAR_EXCEPTION_WINDOW_MAX_DAYS = 731;

export const CalendarExceptionListQuery = z
  .object({
    facility_id: Uuid,
    work_center_id: Uuid.optional(),
    kind: CalendarExceptionKind.optional(),
    /** First facility business date the window covers, inclusive. */
    from: PlanningDate,
    /** Last facility business date the window covers, inclusive. */
    to: PlanningDate,
    cursor: PageCursor,
    limit: PageLimit,
  })
  .superRefine((q, ctx) => {
    if (q.to < q.from) {
      ctx.addIssue({
        code: "custom",
        path: ["to"],
        message: "to may not be before from",
        params: { reason: "to_before_from" },
      });
      return;
    }
    const days = (Date.parse(`${q.to}T00:00:00Z`) - Date.parse(`${q.from}T00:00:00Z`)) / 86_400_000;
    if (days + 1 > CALENDAR_EXCEPTION_WINDOW_MAX_DAYS) {
      ctx.addIssue({
        code: "custom",
        path: ["to"],
        message: `a window spans at most ${String(CALENDAR_EXCEPTION_WINDOW_MAX_DAYS)} days`,
        params: { reason: "window_too_wide" },
      });
    }
  });

export const CalendarExceptionListResponse = z
  .object({ items: z.array(CalendarException), next_cursor: z.string().nullable() })
  .openapi("CalendarExceptionListResponse");

export const CalendarExceptionCreateRequest = z
  .object({
    facility_id: Uuid,
    /** Omitted or null for a facility-wide exception. */
    work_center_id: Uuid.nullable().optional(),
    starts_at: ExceptionInstant,
    ends_at: ExceptionInstant,
    kind: CalendarExceptionKind,
    capacity_factor: CapacityFactor,
    /** At most 500 characters; an empty note is stored as none. */
    note: z.string().trim().max(500).nullable().optional(),
  })
  .strict()
  .openapi("CalendarExceptionCreateRequest");

export const CalendarExceptionIdParam = z.object({ id: Uuid });

/** The reasons an exception is refused once its shape parses (spec 03 §3.3a). */
export const CalendarExceptionInvalidReason = z.enum([
  "kind_unknown",
  "starts_at_malformed",
  "ends_at_malformed",
  "ends_not_after_starts",
  "factor_malformed",
  "factor_out_of_range",
  "factor_above_one_for_kind",
]);

export const CalendarExceptionInvalidProblem = Problem.extend({
  reasons: z.array(CalendarExceptionInvalidReason).optional(),
}).openapi("CalendarExceptionInvalidProblem");

// --- §5 row 36 the facility calendar preview ---------------------------------

/** The most dates one preview reads, and the default (the screen's four-week strip). */
export const FACILITY_CALENDAR_PREVIEW_MAX_DAYS = 62;
export const FACILITY_CALENDAR_PREVIEW_DEFAULT_DAYS = 28;

export const FacilityCalendarPreviewQuery = z
  .object({
    facility_id: Uuid,
    from: CalendarDate,
    days: z.coerce.number().int().min(1).max(FACILITY_CALENDAR_PREVIEW_MAX_DAYS).optional(),
    work_center_id: Uuid.optional(),
  })
  .superRefine((q, ctx) => {
    // The last date read must lie on or before 9999-12-31, as `from` lies on or after 1900-01-01.
    const count = q.days ?? FACILITY_CALENDAR_PREVIEW_DEFAULT_DAYS;
    const last = Date.parse(`${q.from}T00:00:00Z`) + (count - 1) * 86_400_000;
    if (!(last <= Date.parse(`${CALENDAR_MAX_DATE}T00:00:00Z`))) {
      ctx.addIssue({
        code: "custom",
        path: ["days"],
        message: `the window must end by ${CALENDAR_MAX_DATE}`,
        params: { reason: "date_out_of_range" },
      });
    }
  });

/** A stretch of a segment under one set of exceptions. */
export const CalendarPreviewPiece = z
  .object({
    starts_at: Timestamp,
    ends_at: Timestamp,
    wall_minutes: z.number(),
    capacity_factor: CapacityFactor,
    exception_ids: z.array(Uuid),
  })
  .openapi("CalendarPreviewPiece");

export const CalendarPreviewSegment = z
  .object({
    /** `pattern` for a pattern segment, `extra_shift` for time an extra shift adds. */
    source: z.enum(["pattern", "extra_shift"]),
    shift_code: z.string().nullable(),
    efficiency_pct: z.number().int(),
    start: z.string().nullable(),
    end: z.string().nullable(),
    crosses_midnight: z.boolean(),
    starts_at: Timestamp,
    ends_at: Timestamp,
    wall_minutes: z.number(),
    /** Effective minutes, rounded half up to two decimals once from the exact total. */
    effective_minutes: z.number(),
    pieces: z.array(CalendarPreviewPiece),
  })
  .openapi("CalendarPreviewSegment");

export const CalendarPreviewDay = z
  .object({
    date: PlanningDate,
    /** ISO weekday, Monday 1 to Sunday 7. */
    weekday: z.number().int().min(1).max(7),
    /** False when no pattern covers the date. */
    calendared: z.boolean(),
    shift_pattern_id: Uuid.nullable(),
    working: z.boolean(),
    wall_minutes: z.number(),
    effective_minutes: z.number(),
    segments: z.array(CalendarPreviewSegment),
    exception_ids: z.array(Uuid),
    /** The factor-0 exceptions that closed the pattern's time on a non-working date. */
    closing_exception_ids: z.array(Uuid),
  })
  .openapi("CalendarPreviewDay");

export const FacilityPatternRef = z
  .object({ id: Uuid, code: ShiftPatternCode, name: z.string() })
  .openapi("FacilityPatternRef");

export const FacilityCalendarPreview = z
  .object({
    facility_id: Uuid,
    time_zone: z.string(),
    /** The facility calendar's pattern (`planning_settings.facility_shift_pattern_id`), null when none. */
    facility_pattern: FacilityPatternRef.nullable(),
    /** The work centre previewed, null for the facility calendar. */
    work_center_id: Uuid.nullable(),
    from: PlanningDate,
    days: z.array(CalendarPreviewDay),
    /** Every exception a day names, so the strip can label it. */
    exceptions: z.array(CalendarException),
  })
  .openapi("FacilityCalendarPreview");
